'use client';

import { Canvas, useThree, useFrame } from '@react-three/fiber';
import { OrbitControls, Environment } from '@react-three/drei';
import { ConfigureGltfLoaders } from './gltf-loader-setup';
import { ResolvedSceneEnvironment } from './resolved-scene-environment';
import {
  EffectComposer,
  DepthOfField,
  Bloom,
  Vignette,
  ChromaticAberration,
  FXAA,
  N8AO,
  ToneMapping,
} from '@react-three/postprocessing';
import { BlendFunction, ToneMappingMode, Effect } from 'postprocessing';
import { Suspense, type ReactNode, useRef, useEffect, useLayoutEffect, useState, useMemo, useCallback, useImperativeHandle, forwardRef } from 'react';
import * as THREE from 'three';
import { RectAreaLightUniformsLib } from 'three/examples/jsm/lights/RectAreaLightUniformsLib.js';
import { EditorTaaRoot, EditorTemporalResolvePass, useEditorTaaRef } from './editor-taa';
import { IdleSupersampler } from './idle-supersampler';
import { cameraLinkedEnvYaw, getOrbitTarget } from './camera-env-link';
import { NestedTransmissionApplier } from './nested-transmission-applier';
import { patchTransmissionBlurShader, clampTransmissionResolutionScale, patchTransmissionRenderTargetNoMsaa } from './transmission-blur-patch';
import { configureStableTextureSampling, textureHasStableMinFilter, applyMaterialTextureLodBias, ensureTextureLodBiasShaderChunks } from './texture-sampling';
import { getPendingMaterialTextureWork, getActiveGraphTextureJobs } from './material-graph-eval';
import { patchCompileAsyncOnRenderer, patchCompileAsyncSafe } from './compile-async-safe';
import { nativeCanvasMsaa, postFxNeedsComposer, postFxNeedsHdrComposer, postFxUsesRendererToneMap, resolveComposerMsaaSamples, TunableSmaa } from './smaa-effect';

patchTransmissionBlurShader();
ensureTextureLodBiasShaderChunks();
patchTransmissionRenderTargetNoMsaa();
patchCompileAsyncSafe();

RectAreaLightUniformsLib.init();

// Patch Three.js ShaderChunk so bump map stacks on top of normal map
// instead of being mutually exclusive (#elif → separate #ifdef).
(() => {
  const chunk = THREE.ShaderChunk as Record<string, string>;
  const key = 'normal_fragment_maps';
  if (!chunk[key]) return;
  const exclusive = '#elif defined( USE_BUMPMAP )';
  if (chunk[key].includes(exclusive)) {
    chunk[key] = chunk[key].replace(exclusive, '#endif\n#ifdef USE_BUMPMAP');
  }
})();

export interface SnapshotCaptureOptions {
  /** Output width in pixels. Defaults to the live drawing-buffer width. */
  width?: number;
  /** Output height in pixels. Defaults to the live drawing-buffer height. */
  height?: number;
  mimeType?: 'image/png' | 'image/jpeg';
  /** JPEG quality between 0 and 1. Ignored for PNG. */
  quality?: number;
  /**
   * How the live view maps onto a different output aspect ratio.
   * `contain` (default) fits the whole view and pads with the background colour,
   * so nothing is ever cropped. `cover` fills the frame and crops the overflow.
   */
  fit?: 'contain' | 'cover';
}

export interface SnapshotCaptureResult {
  /** Data URL of the encoded image, exactly `width` x `height`. */
  dataUrl: string;
  width: number;
  height: number;
  /** Device pixel ratio the source frame was rendered at. */
  pixelRatio: number;
  /** True when the request exceeded what the viewport could supply and pixels were scaled up. */
  upscaled: boolean;
}

export interface ViewerCanvasHandle {
  /** Current frame at the live canvas resolution. */
  screenshot: () => string | null;
  /** Renders and encodes a frame at an explicit pixel size. */
  captureSnapshot: (options?: SnapshotCaptureOptions) => Promise<SnapshotCaptureResult | null>;
}

export type AutoRotateMode = 'off' | 'rotate' | 'rotate-reset';

export interface ViewerCanvasProps {
  children?: ReactNode;
  environmentPreset?: string;
  hdrUrl?: string;
  backgroundColor?: string;
  shadows?: boolean;
  className?: string;
  autoRotate?: boolean;
  autoRotateMode?: AutoRotateMode;
  autoRotateSpeed?: number;
  onCreated?: () => void;
  cameraPosition?: [number, number, number];
  cameraTarget?: [number, number, number];
  cameraFov?: number;
  cameraNear?: number;
  cameraFar?: number;
  cameraLensShiftX?: number;
  cameraLensShiftY?: number;
  orbitTarget?: [number, number, number];
  /** Minimum perspective-camera distance from the orbit target. */
  cameraMinDistance?: number;
  /** Maximum perspective-camera distance from the orbit target. */
  cameraMaxDistance?: number;
  renderAspect?: number;
  envIntensity?: number;
  envBackgroundBlur?: number;
  envShowBackground?: boolean;
  envLinkToCamera?: boolean;
  envRotationY?: number;
  envFlipH?: boolean;
  envFlipV?: boolean;
  bgMode?: string;
  bgSolidColor?: string;
  bgGradient?: { color1: string; color2: string; type: 'linear' | 'radial'; angle: number };
  bgImageUrl?: string;
  bgImageFit?: 'cover' | 'contain' | 'fill';
  bgImageAdjust?: { brightness?: number; contrast?: number; saturation?: number; temperature?: number };
  postfx?: ViewerPostFX;
  /** Hard reset: visibility / scene cuts — clears temporal history and re-accumulates. */
  postfxTaaResetKey?: string | number;
  /** Soft reset: material/tint swaps — clears history without camera jitter (avoids glass flicker). */
  postfxTaaSoftResetKey?: string | number;
  /** When true, prepare/play the load intro (type selected by scene metadata). */
  introSpinEnabled?: boolean;
  /** Start easing the intro (typically when the viewer fades in). */
  introSpin?: boolean;
  /** Change this when the product/session resets so the intro can play again. */
  introSpinKey?: string | number;
  /** Azimuth spin amount in radians (default ~11°). */
  introSpinAngle?: number;
  /** Duration in seconds (default 1.05). */
  introSpinDuration?: number;
  onSceneStats?: (stats: SceneStats) => void;
  onCameraChange?: (pos: { x: number; y: number; z: number }, target: { x: number; y: number; z: number }) => void;
  /** Device pixel ratio range passed to R3F Canvas (default `[1, 2]`). */
  dpr?: number | [number, number];
  /** Sets `gl.transmissionResolutionScale` for glass/transmission (default `2`). */
  transmissionResolutionScale?: number;
  /** Merged into the Canvas `gl` config after defaults (`antialias`, `alpha`, `preserveDrawingBuffer`). */
  glOverrides?: THREE.WebGLRendererParameters;
  /**
   * Toggle Three.js per-program shader-error checks (`renderer.debug.checkShaderErrors`).
   * Those checks call `getProgramInfoLog`/`getShaderInfoLog` after every link, which force a
   * synchronous CPU↔GPU stall and cause large `requestAnimationFrame`/long-task violations on
   * load. Defaults to enabled only outside production builds. Pass `true` to force-enable for
   * shader debugging.
   */
  debugCheckShaderErrors?: boolean;
}

export interface SceneAaStats {
  /** WebGL context `antialias` flag (requested native MSAA). */
  nativeAntialias: boolean;
  /** Default framebuffer sample count — `0` means geometric MSAA is not on the canvas. */
  samples: number;
  composer: boolean;
  mode: string;
  cssW: number;
  cssH: number;
  bufW: number;
  bufH: number;
  pixelRatio: number;
  lodBias: number;
  requestedMsaa: number;
}

export interface SceneStats {
  /** Total triangles for visible drawables: base geometry × `InstancedMesh.count` per instanced mesh. */
  triangles: number;
  vertices: number;
  meshes: number;
  drawCalls: number;
  textures: number;
  geometries: number;
  /** Triangles from WebGLRenderer.info (last completed frame); post-processing can under-report — see `triangles`. */
  renderTriangles: number;
  renderPoints: number;
  renderLines: number;
  /** Number of linked WebGL programs (shader variant count). */
  programs: number;
  instancedMeshes: number;
  skinnedMeshes: number;
  /** Distinct materials on visible meshes / instanced meshes. */
  materials: number;
  aa?: SceneAaStats;
}

function AutoRotateReset({
  cameraPosition,
  orbitTarget,
  speed,
  delay = 2,
  lerpRate = 0.15,
  rampDuration = 5,
}: {
  cameraPosition?: [number, number, number];
  orbitTarget?: [number, number, number];
  speed: number;
  delay?: number;
  lerpRate?: number;
  rampDuration?: number;
}) {
  const { camera, controls } = useThree();
  const initialSpherical = useRef<THREE.Spherical | null>(null);
  const currentSpherical = useRef(new THREE.Spherical());
  const elapsed = useRef(-1);

  // Reset timer every time the component mounts or props change
  useEffect(() => {
    elapsed.current = 0;
  }, []);

  useEffect(() => {
    elapsed.current = 0;
    if (!cameraPosition || !orbitTarget) return;
    const offset = new THREE.Vector3(
      cameraPosition[0] - orbitTarget[0],
      cameraPosition[1] - orbitTarget[1],
      cameraPosition[2] - orbitTarget[2],
    );
    initialSpherical.current = new THREE.Spherical().setFromVector3(offset);
  }, [cameraPosition, orbitTarget]);

  useFrame((state, delta) => {
    const orbitControls = controls as any;
    if (!orbitControls || !initialSpherical.current) return;

    elapsed.current += delta;
    if (elapsed.current < delay) { state.invalidate(); return; }

    const rampT = Math.min((elapsed.current - delay) / rampDuration, 1);
    const ease = rampT * rampT * (3 - 2 * rampT);
    const factor = Math.min(1, delta * lerpRate * ease);

    const target = orbitControls.target as THREE.Vector3;
    const offset = camera.position.clone().sub(target);
    currentSpherical.current.setFromVector3(offset);

    const init = initialSpherical.current;
    const cur = currentSpherical.current;

    let changed = false;

    const phiDiff = init.phi - cur.phi;
    if (Math.abs(phiDiff) > 0.001) {
      cur.phi += phiDiff * factor;
      changed = true;
    }

    const radiusDiff = init.radius - cur.radius;
    if (Math.abs(radiusDiff) > 0.0001) {
      cur.radius += radiusDiff * factor;
      changed = true;
    }

    if (changed) {
      offset.setFromSpherical(cur);
      camera.position.copy(target).add(offset);
      orbitControls.update();
      state.invalidate();
    }

    orbitControls.autoRotateSpeed = speed;
  });

  return null;
}

function AutoRotateKick({ enabled }: { enabled: boolean }) {
  const invalidate = useThree((s) => s.invalidate);
  useEffect(() => {
    if (enabled) invalidate();
  }, [enabled, invalidate]);

  useFrame((state) => {
    if (enabled) state.invalidate();
  });
  return null;
}

const TWO_PI = Math.PI * 2;

/**
 * drei uses three-stdlib OrbitControls: auto-rotate advances a fixed angle per frame
 * (TWO_PI/60/60 * speed), i.e. it assumes ~60fps. On iPad (~20fps) the orbit is ~3x slower;
 * on high refresh displays without this fix, it runs too fast. We apply the same wall-clock
 * correction for every auto-rotate mode (including rotate-reset) so cycle speeds stay matched.
 */
function AutoRotateWallClockCorrection({ enabled }: { enabled: boolean }) {
  const controls = useThree((s) => s.controls) as any;
  const pointerDownRef = useRef(false);

  useEffect(() => {
    const el = controls?.domElement as HTMLElement | undefined;
    if (!el) return;
    const down = () => {
      pointerDownRef.current = true;
    };
    const up = () => {
      pointerDownRef.current = false;
    };
    el.addEventListener('pointerdown', down);
    el.addEventListener('pointerup', up);
    el.addEventListener('pointercancel', up);
    el.addEventListener('lostpointercapture', up);
    return () => {
      el.removeEventListener('pointerdown', down);
      el.removeEventListener('pointerup', up);
      el.removeEventListener('pointercancel', up);
      el.removeEventListener('lostpointercapture', up);
    };
  }, [controls]);

  useFrame((state, delta) => {
    if (!enabled || !controls?.enabled || pointerDownRef.current) return;
    if (!controls.autoRotate) return;
    const speed = Number(controls.autoRotateSpeed) || 1;
    const desired = (TWO_PI / 60) * speed * delta;
    const fixed = (TWO_PI / 60 / 60) * speed;
    const rev = !!(controls.reverseOrbit || controls.reverseHorizontalOrbit);
    const deltaAz = rev ? desired - fixed : fixed - desired;
    if (Math.abs(deltaAz) < 1e-10) return;
    controls.setAzimuthalAngle(controls.getAzimuthalAngle() + deltaAz);
    state.invalidate();
  }, 0);

  return null;
}

function FovSpeedSync() {
  const camera = useThree((s) => s.camera);
  const controls = useThree((s) => s.controls) as any;

  useFrame(() => {
    if (!controls || !(camera instanceof THREE.PerspectiveCamera)) return;
    const ratio = camera.fov / 50;
    const speed = Math.max(0.5, ratio * 2);
    if (Math.abs((controls.rotateSpeed ?? 1) - speed) > 0.01) {
      controls.rotateSpeed = speed;
      controls.zoomSpeed = Math.max(0.5, ratio * 1.5);
    }
  });

  return null;
}

function CameraPresetListener() {
  const { camera, gl, controls } = useThree();
  const targetPos = useRef<THREE.Vector3 | null>(null);
  const startPos = useRef(new THREE.Vector3());
  const progress = useRef(1);

  useEffect(() => {
    const canvas = gl.domElement;
    const handler = (e: Event) => {
      const pos = (e as CustomEvent).detail as [number, number, number];
      if (pos) {
        startPos.current.copy(camera.position);
        targetPos.current = new THREE.Vector3(...pos);
        progress.current = 0;
      }
    };
    canvas.addEventListener('camera-preset', handler);
    return () => canvas.removeEventListener('camera-preset', handler);
  }, [gl, camera]);

  useFrame((state, delta) => {
    if (!targetPos.current || progress.current >= 1) return;
    progress.current = Math.min(progress.current + delta * 3, 1);
    const t = 1 - Math.pow(1 - progress.current, 3);
    camera.position.lerpVectors(startPos.current, targetPos.current, t);
    const orbitCtrl = controls as any;
    if (orbitCtrl?.target) {
      orbitCtrl.update();
    } else {
      camera.lookAt(0, 0, 0);
    }
    if (progress.current >= 1) targetPos.current = null;
    state.invalidate();
  });

  return null;
}

interface CameraTransitionDetail {
  position: [number, number, number];
  target?: [number, number, number];
  duration?: number;
}

function CameraTransitionListener() {
  const { camera, gl, controls } = useThree();
  const targetPos = useRef<THREE.Vector3 | null>(null);
  const targetLookAt = useRef<THREE.Vector3 | null>(null);
  const startPos = useRef(new THREE.Vector3());
  const startLookAt = useRef(new THREE.Vector3());
  const progress = useRef(1);
  const duration = useRef(0.5);

  useEffect(() => {
    const canvas = gl.domElement;
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<CameraTransitionDetail>).detail;
      if (!detail?.position) return;
      startPos.current.copy(camera.position);
      targetPos.current = new THREE.Vector3(...detail.position);
      if (detail.target) {
        const orbitCtrl = controls as any;
        if (orbitCtrl?.target) {
          startLookAt.current.copy(orbitCtrl.target);
        } else {
          startLookAt.current.set(0, 0, 0);
        }
        targetLookAt.current = new THREE.Vector3(...detail.target);
      } else {
        targetLookAt.current = null;
      }
      duration.current = detail.duration ?? 0.5;
      progress.current = 0;
    };
    canvas.addEventListener('camera-transition', handler);
    return () => canvas.removeEventListener('camera-transition', handler);
  }, [gl, camera, controls]);

  useFrame((state, delta) => {
    if (!targetPos.current || progress.current >= 1) return;
    const d = duration.current > 0 ? duration.current : 0.5;
    progress.current = Math.min(progress.current + delta / d, 1);
    const t = 1 - Math.pow(1 - progress.current, 3);
    state.invalidate();

    camera.position.lerpVectors(startPos.current, targetPos.current, t);

    if (targetLookAt.current) {
      const orbitCtrl = controls as any;
      if (orbitCtrl?.target) {
        orbitCtrl.target.lerpVectors(startLookAt.current, targetLookAt.current, t);
        orbitCtrl.update?.();
      } else {
        const lookAt = startLookAt.current.clone().lerp(targetLookAt.current, t);
        camera.lookAt(lookAt);
      }
    }

    if (progress.current >= 1) {
      targetPos.current = null;
      targetLookAt.current = null;
    }
  });

  return null;
}

/**
 * Slight ease-in orbit on first reveal. Parks at a small azimuth offset under
 * the loader, then eases into the authored pose when `active` — no final-pose flash.
 */
function IntroOrbitSpin({
  enabled = true,
  active,
  spinKey,
  cameraPosition,
  orbitTarget,
  angle = 0.19,
  duration = 1.05,
  onBusyChange,
}: {
  enabled?: boolean;
  active: boolean;
  spinKey?: string | number;
  cameraPosition?: [number, number, number];
  orbitTarget?: [number, number, number];
  angle?: number;
  duration?: number;
  /** Fires when parked/animating so idle SSAA does not accumulate under the loader or mid-spin. */
  onBusyChange?: (busy: boolean) => void;
}) {
  const { camera, controls, scene, gl, invalidate } = useThree();
  const sessionKey = spinKey ?? 'default';
  const preparedKey = useRef<string | number | null>(null);
  const playingKey = useRef<string | number | null>(null);
  const animating = useRef(false);
  const progress = useRef(1);
  const startTheta = useRef(0);
  const endTheta = useRef(0);
  const phi = useRef(0);
  const radius = useRef(0);
  const lookTarget = useRef(new THREE.Vector3());
  const _offset = useRef(new THREE.Vector3());
  const _spherical = useRef(new THREE.Spherical());
  const onBusyChangeRef = useRef(onBusyChange);
  onBusyChangeRef.current = onBusyChange;

  const setBusy = useCallback((busy: boolean) => {
    const prev = !!scene.userData.c3dIntroOrbitBusy;
    scene.userData.c3dIntroOrbitBusy = busy;
    if (prev === busy) return;
    // Defer past any in-flight React render (parent ConfiguratorPlayer).
    queueMicrotask(() => onBusyChangeRef.current?.(busy));
  }, [scene]);

  const applyPoseAtTheta = useCallback((theta: number) => {
    _spherical.current.set(radius.current, phi.current, theta);
    camera.position.copy(lookTarget.current).add(_offset.current.setFromSpherical(_spherical.current));
    const orbit = controls as { target?: THREE.Vector3; update?: () => void } | null;
    if (orbit?.target) {
      orbit.target.copy(lookTarget.current);
      orbit.update?.();
    }
  }, [camera, controls]);

  const prepareFromAuthored = useCallback(() => {
    const orbit = controls as { target?: THREE.Vector3; update?: () => void } | null;
    const ot = orbitTarget
      ? lookTarget.current.set(orbitTarget[0], orbitTarget[1], orbitTarget[2])
      : orbit?.target
        ? lookTarget.current.copy(orbit.target)
        : lookTarget.current.set(0, 0, 0);

    if (!cameraPosition) return false;
    _offset.current.set(
      cameraPosition[0] - ot.x,
      cameraPosition[1] - ot.y,
      cameraPosition[2] - ot.z,
    );
    _spherical.current.setFromVector3(_offset.current);
    if (_spherical.current.radius < 1e-4) return false;

    endTheta.current = _spherical.current.theta;
    startTheta.current = _spherical.current.theta + angle;
    phi.current = _spherical.current.phi;
    radius.current = _spherical.current.radius;
    return true;
  }, [angle, cameraPosition, controls, orbitTarget]);

  const finish = useCallback(() => {
    animating.current = false;
    progress.current = 1;
    setBusy(false);
  }, [setBusy]);

  // Disable: clear busy and forget prepare so CameraSync owns the pose.
  useLayoutEffect(() => {
    if (enabled) return;
    preparedKey.current = null;
    playingKey.current = null;
    finish();
  }, [enabled, finish]);

  // Park at spin-start before fade-in (no flash of the final angle).
  useLayoutEffect(() => {
    if (!enabled) return;
    if (preparedKey.current === sessionKey) return;
    if (!cameraPosition) return;
    if (!prepareFromAuthored()) return;

    setBusy(true);
    applyPoseAtTheta(startTheta.current);
    preparedKey.current = sessionKey;
    playingKey.current = null;
    animating.current = false;
    progress.current = 0;
    invalidate();
  }, [enabled, sessionKey, cameraPosition, prepareFromAuthored, applyPoseAtTheta, setBusy, invalidate]);

  useLayoutEffect(() => {
    if (!enabled) return;
    if (preparedKey.current != null && preparedKey.current !== sessionKey) {
      preparedKey.current = null;
      playingKey.current = null;
      finish();
    }
  }, [enabled, sessionKey, finish]);

  useEffect(() => {
    if (!enabled || !active) return;
    if (preparedKey.current !== sessionKey) return;
    if (playingKey.current === sessionKey) return;
    playingKey.current = sessionKey;
    animating.current = true;
    progress.current = 0;
    setBusy(true);
    applyPoseAtTheta(startTheta.current);
    invalidate();
  }, [enabled, active, sessionKey, applyPoseAtTheta, invalidate, setBusy]);

  useEffect(() => {
    const el = gl.domElement;
    const cancel = () => {
      if (animating.current && progress.current < 1) finish();
    };
    el.addEventListener('pointerdown', cancel);
    return () => el.removeEventListener('pointerdown', cancel);
  }, [gl, finish]);

  useEffect(() => () => {
    scene.userData.c3dIntroOrbitBusy = false;
    queueMicrotask(() => onBusyChangeRef.current?.(false));
  }, [scene]);

  // Priority -3: move the camera *before* TAA motion-break / jitter / render so
  // idle SSAA never folds a mid-orbit pose into history on the same frame.
  useFrame((state, delta) => {
    if (!enabled) return;

    if (preparedKey.current === sessionKey && !animating.current && progress.current < 1) {
      applyPoseAtTheta(startTheta.current);
      state.invalidate();
      return;
    }

    if (!animating.current || progress.current >= 1) return;
    const dur = Math.max(0.35, duration);
    progress.current = Math.min(progress.current + delta / dur, 1);
    const t = 1 - Math.pow(1 - progress.current, 2.6);
    const theta = THREE.MathUtils.lerp(startTheta.current, endTheta.current, t);
    applyPoseAtTheta(theta);
    if (progress.current >= 1) {
      animating.current = false;
      setBusy(false);
    }
    state.invalidate();
  }, -3);

  return null;
}

export function ObjectRotationGroup({ enabled, children }: { enabled: boolean; children: ReactNode }) {
  const groupRef = useRef<THREE.Group>(null!);
  const { camera } = useThree();
  const spherical = useRef(new THREE.Spherical());

  useFrame(() => {
    if (!groupRef.current) return;
    if (!enabled) {
      if (groupRef.current.rotation.y !== 0) groupRef.current.rotation.y = 0;
      return;
    }
    spherical.current.setFromVector3(camera.position);
    groupRef.current.rotation.y = spherical.current.theta;
  });

  return <group ref={groupRef}>{children}</group>;
}

function EnvironmentRotationSync({
  enabled,
  rotationY = 0,
  flipH = false,
  flipV = false,
}: {
  enabled: boolean;
  rotationY?: number;
  flipH?: boolean;
  flipV?: boolean;
}) {
  const { scene, camera, controls } = useThree();

  useFrame(() => {
    const baseY = rotationY + (flipH ? Math.PI : 0);
    const baseX = flipV ? Math.PI : 0;

    let targetY = baseY;
    if (enabled) {
      targetY += cameraLinkedEnvYaw(camera, getOrbitTarget(controls));
    }

    if (!scene.environmentRotation) {
      (scene as any).environmentRotation = new THREE.Euler(baseX, targetY, 0);
    } else {
      scene.environmentRotation.x = baseX;
      scene.environmentRotation.y = targetY;
    }

    if (scene.backgroundRotation) {
      scene.backgroundRotation.x = baseX;
      scene.backgroundRotation.y = targetY;
    } else {
      (scene as any).backgroundRotation = new THREE.Euler(baseX, targetY, 0);
    }
  });

  return null;
}

function hfovToVfov(hDeg: number, aspect: number): number {
  const hRad = (hDeg * Math.PI) / 180;
  return (2 * Math.atan(Math.tan(hRad / 2) / aspect) * 180) / Math.PI;
}

/** Match editor ViewportSync: horizontal FOV is authored for render/safe-frame aspect, not the live canvas. */
function cameraEffectiveAspect(pc: THREE.PerspectiveCamera, renderAspect?: number): number {
  if (renderAspect && renderAspect > 0) return renderAspect;
  if (pc.aspect > 0) return pc.aspect;
  return 16 / 9;
}

/** DevTools / fullscreen / panel chrome changes canvas size — drop temporal AA history. */
function CanvasViewportHistoryReset({
  onViewportResize,
}: {
  onViewportResize: () => void;
}) {
  const size = useThree((s) => s.size);
  const invalidate = useThree((s) => s.invalidate);
  const prev = useRef({ w: 0, h: 0 });
  useEffect(() => {
    const w = Math.round(size.width);
    const h = Math.round(size.height);
    if (w < 1 || h < 1) return;
    if (prev.current.w < 1) {
      prev.current = { w, h };
      return;
    }
    if (prev.current.w !== w || prev.current.h !== h) {
      prev.current = { w, h };
      onViewportResize();
      invalidate();
    }
  }, [size.width, size.height, onViewportResize, invalidate]);
  return null;
}

function CameraSync({
  cameraPosition,
  cameraTarget,
  cameraFov,
  cameraNear,
  cameraFar,
  cameraLensShiftX,
  cameraLensShiftY,
  orbitTarget,
  renderAspect,
  onCameraChange,
}: {
  cameraPosition?: [number, number, number];
  cameraTarget?: [number, number, number];
  cameraFov?: number;
  cameraNear?: number;
  cameraFar?: number;
  cameraLensShiftX?: number;
  cameraLensShiftY?: number;
  orbitTarget?: [number, number, number];
  renderAspect?: number;
  onCameraChange?: ViewerCanvasProps['onCameraChange'];
}) {
  const { camera, controls, scene } = useThree();
  const targetPos = useRef<THREE.Vector3 | null>(null);
  const targetLookAt = useRef<THREE.Vector3 | null>(null);
  const progress = useRef(1);
  const isUserDragging = useRef(false);
  const lastEmit = useRef(0);
  const shiftRef = useRef({ x: 0, y: 0 });
  const hfovRef = useRef(0);

  const sx = cameraLensShiftX ?? 0;
  const sy = cameraLensShiftY ?? 0;

  useEffect(() => {
    if (!cameraPosition || isUserDragging.current) return;
    targetPos.current = new THREE.Vector3(...cameraPosition);
    progress.current = 0;
  }, [cameraPosition?.[0], cameraPosition?.[1], cameraPosition?.[2]]);

  useEffect(() => {
    const effectiveTarget = orbitTarget || cameraTarget;
    if (!effectiveTarget || isUserDragging.current) return;
    targetLookAt.current = new THREE.Vector3(...effectiveTarget);
    progress.current = 0;
  }, [orbitTarget?.[0], orbitTarget?.[1], orbitTarget?.[2], cameraTarget?.[0], cameraTarget?.[1], cameraTarget?.[2]]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!(camera as THREE.PerspectiveCamera).isPerspectiveCamera) return;
    const pc = camera as THREE.PerspectiveCamera;
    let changed = false;
    if (cameraFov && cameraFov > 0) {
      hfovRef.current = cameraFov;
      const effectiveAspect = cameraEffectiveAspect(pc, renderAspect);
      pc.fov = hfovToVfov(cameraFov, effectiveAspect);
      changed = true;
    }
    if (cameraNear && cameraNear > 0) { pc.near = Math.max(cameraNear, 0.01); changed = true; }
    if (cameraFar && cameraFar > 0) { pc.far = Math.min(cameraFar, 500); changed = true; }
    if (changed) {
      pc.updateProjectionMatrix();
      if (sx !== 0 || sy !== 0) {
        const m = pc.projectionMatrix.elements;
        m[8] += sx * 2;
        m[9] -= sy * 2;
      }
      shiftRef.current = { x: sx, y: sy };
    }
  }, [cameraFov, cameraNear, cameraFar, camera, sx, sy, renderAspect]);

  useEffect(() => {
    const orbitControls = controls as any;
    if (!orbitControls || !onCameraChange) return;

    const handleStart = () => { isUserDragging.current = true; };
    const handleEnd = () => { isUserDragging.current = false; };

    const handleChange = () => {
      if (!isUserDragging.current) return;
      const now = Date.now();
      if (now - lastEmit.current < 100) return;
      lastEmit.current = now;

      const pos = camera.position;
      const tgt = orbitControls.target as THREE.Vector3;
      onCameraChange(
        { x: +pos.x.toFixed(2), y: +pos.y.toFixed(2), z: +pos.z.toFixed(2) },
        { x: +tgt.x.toFixed(2), y: +tgt.y.toFixed(2), z: +tgt.z.toFixed(2) },
      );
    };

    orbitControls.addEventListener('start', handleStart);
    orbitControls.addEventListener('end', handleEnd);
    orbitControls.addEventListener('change', handleChange);
    return () => {
      orbitControls.removeEventListener('start', handleStart);
      orbitControls.removeEventListener('end', handleEnd);
      orbitControls.removeEventListener('change', handleChange);
    };
  }, [controls, camera, onCameraChange]);

  useFrame((state, delta) => {
    const introBusy = !!scene.userData.c3dIntroOrbitBusy;
    if (!introBusy && !(progress.current >= 1 || isUserDragging.current)) {
      progress.current = Math.min(progress.current + delta * 4, 1);
      const t = 1 - Math.pow(1 - progress.current, 3);

      if (targetPos.current) {
        camera.position.lerp(targetPos.current, t);
        if (progress.current >= 1) targetPos.current = null;
      }

      const orbitControls = controls as any;
      if (targetLookAt.current && orbitControls?.target) {
        (orbitControls.target as THREE.Vector3).lerp(targetLookAt.current, t);
        orbitControls.update();
        if (progress.current >= 1) targetLookAt.current = null;
      }
      state.invalidate();
    }

    if (!(camera as THREE.PerspectiveCamera).isPerspectiveCamera) return;
    const pc = camera as THREE.PerspectiveCamera;

    const hfov = hfovRef.current;
    const { x: shiftX, y: shiftY } = shiftRef.current;
    const hasShift = shiftX !== 0 || shiftY !== 0;
    let needsUpdate = false;

    if (hfov > 0) {
      const effectiveAspect = cameraEffectiveAspect(pc, renderAspect);
      const vfov = hfovToVfov(hfov, effectiveAspect);
      if (Math.abs(pc.fov - vfov) > 0.01) {
        pc.fov = vfov;
        needsUpdate = true;
      }
    }

    if (hasShift) {
      const m = pc.projectionMatrix.elements;
      if (Math.abs(m[8] - shiftX * 2) > 0.0001 || Math.abs(m[9] - (-shiftY * 2)) > 0.0001) {
        needsUpdate = true;
      }
    }

    if (needsUpdate) {
      pc.updateProjectionMatrix();
      if (hasShift) {
        const m = pc.projectionMatrix.elements;
        m[8] += shiftX * 2;
        m[9] -= shiftY * 2;
      }
      state.invalidate();
    }
  });

  return null;
}

function SceneStatsReporter({
  onStats,
  postfx,
}: {
  onStats: (stats: SceneStats) => void;
  postfx?: {
    enabled?: boolean;
    antialiasing?: string;
    textureLodBias?: number;
    outputMsaaSamples?: number;
    dof?: { enabled?: boolean };
    bloom?: { enabled?: boolean };
    vignette?: { enabled?: boolean };
    chromaticAberration?: { enabled?: boolean };
    ssao?: { enabled?: boolean };
    sharpenBlur?: { enabled?: boolean };
  };
}) {
  const { scene, gl, size } = useThree();
  const tickRef = useRef(0);
  const prevJsonRef = useRef('');

  useFrame((state) => {
    tickRef.current++;
    // First samples are noisy; then refresh ~2×/s at 60fps for optimization work without spamming React.
    if (tickRef.current < 8) {
      state.invalidate();
      return;
    }
    if ((tickRef.current - 8) % 30 !== 0) return;

    let triangles = 0;
    let vertices = 0;
    let meshCount = 0;
    let instancedMeshes = 0;
    let skinnedMeshes = 0;
    const materialIds = new Set<string>();

    const addDrawableMesh = (obj: THREE.Mesh, instanceMultiplier = 1) => {
      meshCount++;
      const mult = Math.max(1, instanceMultiplier);
      const geo = obj.geometry;
      if (!geo) return;
      const idx = geo.index;
      const baseTris = idx ? idx.count / 3 : (geo.attributes.position?.count ?? 0) / 3;
      triangles += baseTris * mult;
      const pos = geo.attributes.position;
      if (pos) vertices += pos.count * mult;
      const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
      for (const m of mats) if (m) materialIds.add(m.uuid);
    };

    scene.traverse((child) => {
      if (!child.visible) return;
      if (child instanceof THREE.InstancedMesh) {
        instancedMeshes++;
        addDrawableMesh(child, child.count);
        return;
      }
      if (child instanceof THREE.SkinnedMesh) {
        skinnedMeshes++;
        addDrawableMesh(child);
        return;
      }
      if (child instanceof THREE.Mesh) addDrawableMesh(child);
    });

    const info = gl.info;
    const render = info.render;
    const sceneTriangles = Math.round(triangles);
    let renderTriangles = Math.round(render?.triangles ?? 0);
    // EffectComposer / extra passes often leave info.render.triangles near zero; fall back to scene tally.
    if (sceneTriangles > 500 && renderTriangles < sceneTriangles * 0.05) {
      renderTriangles = sceneTriangles;
    }
    const ctx = gl.getContext() as WebGLRenderingContext | WebGL2RenderingContext;
    const attrs = gl.getContextAttributes?.() ?? ctx.getContextAttributes?.();
    let samples = 0;
    try {
      samples = Number(ctx.getParameter(ctx.SAMPLES)) || 0;
    } catch {
      samples = 0;
    }
    const composer = postFxNeedsComposer(postfx) || postfx?.antialiasing === 'taa';
    const bufW = gl.domElement.width;
    const bufH = gl.domElement.height;
    const cssW = Math.round(size.width);
    const cssH = Math.round(size.height);
    const aa: SceneAaStats = {
      nativeAntialias: !!attrs?.antialias,
      samples,
      composer,
      mode: String(postfx?.antialiasing ?? 'none'),
      cssW,
      cssH,
      bufW,
      bufH,
      pixelRatio: cssW > 0 ? Math.round((bufW / cssW) * 100) / 100 : gl.getPixelRatio(),
      lodBias: typeof postfx?.textureLodBias === 'number' ? postfx.textureLodBias : 0,
      requestedMsaa: postfx?.outputMsaaSamples ?? 0,
    };
    if (typeof window !== 'undefined') {
      (window as unknown as { __c3dAa?: SceneAaStats }).__c3dAa = aa;
    }

    const stats: SceneStats = {
      triangles: sceneTriangles,
      vertices: Math.round(vertices),
      meshes: meshCount,
      drawCalls: render?.calls ?? 0,
      textures: info.memory?.textures ?? 0,
      geometries: info.memory?.geometries ?? 0,
      renderTriangles,
      renderPoints: render?.points ?? 0,
      renderLines: render?.lines ?? 0,
      programs: info.programs?.length ?? 0,
      instancedMeshes,
      skinnedMeshes,
      materials: materialIds.size,
      aa,
    };

    const json = JSON.stringify(stats);
    if (json !== prevJsonRef.current) {
      prevJsonRef.current = json;
      onStats(stats);
    }
  });

  useEffect(() => {
    tickRef.current = 0;
    prevJsonRef.current = '';
  }, [onStats]);

  return null;
}

export interface ViewerPostFX {
  enabled?: boolean;
  toneMapping?: string;
  exposure?: number;
  dof?: { enabled: boolean; focusDistance: number; focalLength: number; bokehScale: number };
  bloom?: { enabled: boolean; intensity: number; threshold: number; radius: number };
  vignette?: { enabled: boolean; offset: number; darkness: number };
  chromaticAberration?: { enabled: boolean; offset: number };
  ssao?: { enabled: boolean; radius: number; intensity: number; samples?: number; denoiseSamples?: number; denoiseRadius?: number; distanceFalloff?: number; screenSpaceRadius?: boolean; lightnessOnly?: boolean; excludeActive?: boolean };
  sharpenBlur?: { enabled: boolean; sharpenAmount: number; sharpenRadius: number; blurRadius: number };
  antialiasing?: 'none' | 'smaa' | 'msaa' | 'fxaa' | 'taa';
  canvasAntialias?: boolean;
  outputMsaaSamples?: number;
  smaaPreset?: string;
  dprMin?: number;
  dprMax?: number;
  textureMaxAnisotropy?: number;
  /**
   * Positive mip LOD bias for material maps (brushed metal / weave moiré).
   * 0 = stock Three sampling. ~0.5–1.0 softens high-freq patterns before temporal AA.
   */
  textureLodBias?: number;
}

function getToneMappingMode(name?: string): ToneMappingMode {
  switch (name) {
    case 'linear': return ToneMappingMode.LINEAR;
    case 'reinhard': return ToneMappingMode.REINHARD;
    case 'reinhard2': return ToneMappingMode.REINHARD2;
    case 'reinhard2_adaptive': return ToneMappingMode.REINHARD2_ADAPTIVE;
    case 'cineon': return ToneMappingMode.OPTIMIZED_CINEON;
    case 'aces': return ToneMappingMode.ACES_FILMIC;
    case 'filmic': return ToneMappingMode.ACES_FILMIC;
    case 'agx': return ToneMappingMode.AGX;
    case 'neutral': return ToneMappingMode.NEUTRAL;
    default: return ToneMappingMode.LINEAR;
  }
}

function getThreeToneMapping(name?: string): THREE.ToneMapping {
  switch (name) {
    case 'reinhard':
      return THREE.ReinhardToneMapping;
    case 'agx':
      return THREE.AgXToneMapping;
    case 'neutral':
      return THREE.NeutralToneMapping;
    case 'aces':
    case 'filmic':
      return THREE.ACESFilmicToneMapping;
    default:
      return THREE.LinearToneMapping;
  }
}

// ---------------------------------------------------------------------------
// N8AO HSL lightness-only shader patch
// ---------------------------------------------------------------------------
const N8AO_HSL_FUNCTIONS = /* glsl */`
vec3 _n8aoRgb2hsl(vec3 c) {
  float mx = max(max(c.r, c.g), c.b);
  float mn = min(min(c.r, c.g), c.b);
  float l = (mx + mn) * 0.5;
  if (mx == mn) return vec3(0.0, 0.0, l);
  float d = mx - mn;
  float s = l > 0.5 ? d / (2.0 - mx - mn) : d / (mx + mn);
  float h;
  if (mx == c.r) h = (c.g - c.b) / d + (c.g < c.b ? 6.0 : 0.0);
  else if (mx == c.g) h = (c.b - c.r) / d + 2.0;
  else h = (c.r - c.g) / d + 4.0;
  return vec3(h / 6.0, s, l);
}
float _n8aoHue2rgb(float p, float q, float t) {
  if (t < 0.0) t += 1.0;
  if (t > 1.0) t -= 1.0;
  if (t < 1.0/6.0) return p + (q - p) * 6.0 * t;
  if (t < 0.5) return q;
  if (t < 2.0/3.0) return p + (q - p) * (2.0/3.0 - t) * 6.0;
  return p;
}
vec3 _n8aoHsl2rgb(vec3 hsl) {
  if (hsl.y <= 0.0) return vec3(hsl.z);
  float q = hsl.z < 0.5 ? hsl.z * (1.0 + hsl.y) : hsl.z + hsl.y - hsl.z * hsl.y;
  float p = 2.0 * hsl.z - q;
  return vec3(
    _n8aoHue2rgb(p, q, hsl.x + 1.0/3.0),
    _n8aoHue2rgb(p, q, hsl.x),
    _n8aoHue2rgb(p, q, hsl.x - 1.0/3.0)
  );
}
vec3 _n8aoLightnessAO(vec3 rgb, float ao) {
  vec3 hsl = _n8aoRgb2hsl(rgb);
  hsl.z *= ao;
  return _n8aoHsl2rgb(hsl);
}
`;

// ---------------------------------------------------------------------------
// Custom sharpen + blur effect (single-pass unsharp mask / Gaussian blur)
// ---------------------------------------------------------------------------
const SHARPEN_BLUR_FRAG = `
uniform float u_sharpenAmount;
uniform float u_sharpenRadius;
uniform float u_blurRadius;

void mainImage(const in vec4 inputColor, const in vec2 uv, out vec4 outputColor) {
  vec2 texelSize = 1.0 / resolution;

  // Gaussian-style blur (9-tap)
  float bR = u_blurRadius;
  vec4 blurred = inputColor * 0.2270270270;
  blurred += texture2D(inputBuffer, uv + vec2( bR, 0.0) * texelSize) * 0.1945945946;
  blurred += texture2D(inputBuffer, uv + vec2(-bR, 0.0) * texelSize) * 0.1945945946;
  blurred += texture2D(inputBuffer, uv + vec2(0.0,  bR) * texelSize) * 0.1945945946;
  blurred += texture2D(inputBuffer, uv + vec2(0.0, -bR) * texelSize) * 0.1945945946;

  // If only blur is active (no sharpening), output the blurred result
  if (u_sharpenAmount <= 0.0) {
    outputColor = (bR > 0.0) ? blurred : inputColor;
    return;
  }

  // Unsharp mask: sharpen by subtracting the blurred version from the original
  float sR = u_sharpenRadius;
  vec4 sBlur = inputColor * 0.2270270270;
  sBlur += texture2D(inputBuffer, uv + vec2( sR, 0.0) * texelSize) * 0.1945945946;
  sBlur += texture2D(inputBuffer, uv + vec2(-sR, 0.0) * texelSize) * 0.1945945946;
  sBlur += texture2D(inputBuffer, uv + vec2(0.0,  sR) * texelSize) * 0.1945945946;
  sBlur += texture2D(inputBuffer, uv + vec2(0.0, -sR) * texelSize) * 0.1945945946;

  vec4 sharpened = inputColor + u_sharpenAmount * (inputColor - sBlur);

  // Apply blur on top of sharpened result if blur is active
  if (bR > 0.0) {
    vec4 finalBlur = sharpened * 0.2270270270;
    finalBlur += texture2D(inputBuffer, uv + vec2( bR, 0.0) * texelSize) * 0.1945945946;
    finalBlur += texture2D(inputBuffer, uv + vec2(-bR, 0.0) * texelSize) * 0.1945945946;
    finalBlur += texture2D(inputBuffer, uv + vec2(0.0,  bR) * texelSize) * 0.1945945946;
    finalBlur += texture2D(inputBuffer, uv + vec2(0.0, -bR) * texelSize) * 0.1945945946;
    outputColor = finalBlur;
  } else {
    outputColor = sharpened;
  }
}
`;

class SharpenBlurEffect extends Effect {
  constructor({ sharpenAmount = 1.0, sharpenRadius = 0.5, blurRadius = 0 } = {}) {
    super('SharpenBlurEffect', SHARPEN_BLUR_FRAG, {
      blendFunction: BlendFunction.NORMAL,
      uniforms: new Map<string, THREE.Uniform>([
        ['u_sharpenAmount', new THREE.Uniform(sharpenAmount)],
        ['u_sharpenRadius', new THREE.Uniform(sharpenRadius)],
        ['u_blurRadius', new THREE.Uniform(blurRadius)],
      ]),
    });
  }
}

const SharpenBlurPass = forwardRef(function SharpenBlurPassInner(
  { sharpenAmount, sharpenRadius, blurRadius }: {
    sharpenAmount: number; sharpenRadius: number; blurRadius: number;
  },
  ref: React.Ref<SharpenBlurEffect>,
) {
  const effect = useMemo(
    () => new SharpenBlurEffect({ sharpenAmount, sharpenRadius, blurRadius }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    effect.uniforms.get('u_sharpenAmount')!.value = sharpenAmount;
    effect.uniforms.get('u_sharpenRadius')!.value = sharpenRadius;
    effect.uniforms.get('u_blurRadius')!.value = blurRadius;
  }, [effect, sharpenAmount, sharpenRadius, blurRadius]);

  return <primitive ref={ref} object={effect} dispose={null} />;
});

function TextureReadyListener() {
  const inv = useThree((s) => s.invalidate);
  useEffect(() => {
    const h = () => inv();
    document.addEventListener('scene-texture-ready', h);
    document.addEventListener('scene-graph-textures-idle', h);
    document.addEventListener('scene-material-maps-ready', h);
    return () => {
      document.removeEventListener('scene-texture-ready', h);
      document.removeEventListener('scene-graph-textures-idle', h);
      document.removeEventListener('scene-material-maps-ready', h);
    };
  }, [inv]);
  return null;
}

/** Pause idle SSAA / temporal resolve while graph CPU bakes or image loads are in flight. */
function GraphTextureBusyTracker({ onBusyChange }: { onBusyChange: (busy: boolean) => void }) {
  useEffect(() => {
    let last: boolean | null = null;
    let pending = false;
    const read = () => getPendingMaterialTextureWork() > 0 || getActiveGraphTextureJobs() > 0;
    const flush = () => {
      pending = false;
      const busy = read();
      if (busy === last) return;
      last = busy;
      onBusyChange(busy);
    };
    // Graph eval dispatches these events synchronously during GlbModel render.
    // Never call setState on ViewerCanvas in that stack — React throws
    // "Cannot update ViewerCanvas while rendering ConfiguratorPlayer".
    const sync = () => {
      if (pending) return;
      pending = true;
      queueMicrotask(flush);
    };
    flush();
    document.addEventListener('scene-texture-ready', sync);
    document.addEventListener('scene-graph-textures-idle', sync);
    document.addEventListener('scene-material-maps-ready', sync);
    return () => {
      document.removeEventListener('scene-texture-ready', sync);
      document.removeEventListener('scene-graph-textures-idle', sync);
      document.removeEventListener('scene-material-maps-ready', sync);
    };
  }, [onBusyChange]);
  return null;
}

/**
 * Patch GL program queries before any material/EffectComposer compile runs.
 * Must be the first Canvas child so useLayoutEffect fires ahead of other mounts.
 */
function ViewerGlProgramGuard() {
  const gl = useThree((s) => s.gl);
  useLayoutEffect(() => {
    patchCompileAsyncOnRenderer(gl);
    try {
      gl.debug.checkShaderErrors = false;
    } catch {
      /* ignore */
    }
  }, [gl]);
  return null;
}

/**
 * Warms the WebGL program cache off the blocking render path.
 * Disabled in the viewer: compileAsync + material-swap races still hit
 * `glGetProgramiv: Program object expected` in Chromium even with a safe poller,
 * and the hitch on first draw is preferable to persistent console spam.
 */
function ShaderPrecompiler() {
  return null;
}

const VIEWER_MATERIAL_MAP_TEXTURE_KEYS = [
  'map', 'lightMap', 'aoMap', 'emissiveMap', 'bumpMap', 'normalMap',
  'displacementMap', 'roughnessMap', 'metalnessMap', 'alphaMap',
  'clearcoatMap', 'clearcoatNormalMap', 'clearcoatRoughnessMap',
  'sheenColorMap', 'sheenRoughnessMap', 'specularColorMap', 'specularIntensityMap',
  'transmissionMap', 'thicknessMap', 'anisotropyMap',
  'iridescenceMap', 'iridescenceThicknessMap',
] as const;

function viewerPickMsaaSamples(requested: number | undefined): number {
  const allowed = [0, 2, 4, 8, 16];
  const v = Math.max(0, Math.min(16, Math.round(requested ?? 0)));
  return allowed.reduce((best, cur) => (Math.abs(cur - v) < Math.abs(best - v) ? cur : best));
}

/**
 * Half-float MSAA RTs routinely fail with
 * `glRenderbufferStorageMultisample: Texture total allocation size is too large`
 * (then incomplete zero-size FBOs). Cap samples by drawing-buffer area so AA
 * actually allocates instead of silently becoming zero.
 */
function viewerCapMsaaForBuffer(samples: number, gl: THREE.WebGLRenderer): number {
  if (samples <= 0) return 0;
  const size = new THREE.Vector2();
  gl.getDrawingBufferSize(size);
  const px = Math.max(1, size.x * size.y);
  // Byte buffers: 4B/px/sample (+resolve). ~11MP @ 8× is OK on desktop; 4× above that.
  if (px > 16_000_000) return Math.min(samples, 4);
  return Math.min(samples, 8);
}

/**
 * EffectComposer replaces the canvas backbuffer — WebGL context `antialias`
 * never touches the scene color. Drive composer multisampling for both `msaa`
 * and `smaa` modes (SMAA cleans residual crawl on top). Temporal history AA is
 * off on this product (glass ghosts); quality is constant high DPR + MSAA/SMAA.
 *
 * Critical: default EffectComposer uses HalfFloatType. Half-float + MSAA is what
 * triggered the allocation failures / zero-size FBOs — MSAA must use UnsignedByte.
 */
function viewerResolveComposerMultisampling(
  aa: ViewerPostFX['antialiasing'] | undefined,
  settings: ViewerPostFX,
  gl: THREE.WebGLRenderer,
): number {
  if (aa !== 'msaa' && aa !== 'smaa') return 0;
  // 8× on the composer RT; SMAA then blends leftover thin gold rims.
  // Hard-capping at 4× left cap/neck/cage silhouettes stair-stepped at 2× DPR.
  const requested = viewerPickMsaaSamples(resolveComposerMsaaSamples(aa, settings.outputMsaaSamples));
  const capped = viewerCapMsaaForBuffer(requested, gl);
  const maxSamples = typeof gl.capabilities?.maxSamples === 'number' ? gl.capabilities.maxSamples : capped;
  return Math.max(0, Math.min(capped, maxSamples || capped, 8));
}

function ViewerTextureAnisotropyCap({
  maxAnisotropy,
  lodBias = 0,
}: {
  maxAnisotropy: number;
  lodBias?: number;
}) {
  const { gl, scene } = useThree();
  const cap = Math.max(1, Math.min(maxAnisotropy, gl.capabilities.getMaxAnisotropy()));
  useFrame(() => {
    scene.traverse((obj) => {
      if (!(obj instanceof THREE.Mesh)) return;
      const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
      for (const mat of mats) {
        if (!mat || typeof mat !== 'object') continue;
        applyMaterialTextureLodBias(mat as THREE.Material, lodBias);
        for (const key of VIEWER_MATERIAL_MAP_TEXTURE_KEYS) {
          const t = (mat as Record<string, unknown>)[key];
          if (t && (t as THREE.Texture).isTexture) {
            const tex = t as THREE.Texture;
            // Mips first — anisotropy is ignored for non-mip minFilters in modern Three.
            if (!textureHasStableMinFilter(tex)) configureStableTextureSampling(tex);
            if (tex.anisotropy !== cap) tex.anisotropy = cap;
          }
        }
      }
    });
  });
  return null;
}

/**
 * Cancels idle accumulation the instant the camera transform actually changes.
 * OrbitControls events cover mouse orbits, but programmatic motion (intro spin,
 * view-preset transitions, damping settle) can bypass them — this catches all of
 * them by watching the world matrix, so accumulation never smears across motion.
 * Sub-pixel jitter uses the projection offset (not position/quaternion), so the
 * running accumulation itself is not detected as motion.
 *
 * Thresholds are intentionally loose: FP noise / damping micro-settle used to trip
 * 1e-10 every frame, flip `isIdle` off, and never re-arm — idle AA looked random.
 */
function ViewerIdleMotionGuard({ onMotion }: { onMotion: () => void }) {
  const camera = useThree((s) => s.camera);
  const prevPos = useRef<THREE.Vector3 | null>(null);
  const prevQuat = useRef<THREE.Quaternion | null>(null);
  useFrame(() => {
    const p = camera.position;
    const q = camera.quaternion;
    const pp = prevPos.current;
    const pq = prevQuat.current;
    if (
      pp &&
      pq &&
      (pp.distanceToSquared(p) > 1e-7 || Math.abs(pq.dot(q)) < 0.999995)
    ) {
      onMotion();
    }
    if (!pp) prevPos.current = p.clone();
    else pp.copy(p);
    if (!pq) prevQuat.current = q.clone();
    else pq.copy(q);
  }, -2);
  return null;
}

function ViewerPostProcessing({ settings, taaResolve = false }: { settings: ViewerPostFX; taaResolve?: boolean }) {
  const size = useThree((s) => s.size);
  const gl = useThree((s) => s.gl);
  const inv = useThree((s) => s.invalidate);
  const taaRef = useEditorTaaRef();
  const n8aoRef = useRef<any>(null);
  const ssaoLightnessOnly = settings.ssao?.lightnessOnly ?? false;
  const ssaoExcludeActive = settings.ssao?.excludeActive ?? false;

  useEffect(() => { inv(); }, [settings, inv]);

  useFrame(() => {
    const pass = n8aoRef.current;
    if (!pass?.effectCompositerQuad?.material) return;

    if (ssaoExcludeActive && pass.configuration && !pass.configuration.transparencyAware) {
      pass.configuration.transparencyAware = true;
    }

    const mat = pass.effectCompositerQuad.material;
    const patched = mat.fragmentShader.includes('_n8aoRgb2hsl');
    if (ssaoLightnessOnly && !patched) {
      let fs = mat.fragmentShader;
      fs = fs.replace('void main() {', N8AO_HSL_FUNCTIONS + '\nvoid main() {');
      fs = fs.replace(/mix\(sceneTexel\.rgb,\s*aoApplied,\s*1\.0\s*-\s*finalAo\)/g, '_n8aoLightnessAO(sceneTexel.rgb, finalAo)');
      fs = fs.replace(/mix\(vec3\(1\.0\),\s*aoApplied,\s*1\.0\s*-\s*finalAo\)/g, 'vec3(finalAo)');
      mat.fragmentShader = fs;
      mat.needsUpdate = true;
    } else if (!ssaoLightnessOnly && patched) {
      pass.configureEffectCompositer?.(
        pass.configuration?.depthBufferType,
        pass.camera?.isOrthographicCamera,
      );
    }
  });

  if (size.width < 4 || size.height < 4) return null;

  const aa = settings.antialiasing ?? 'none';
  const wantAa = aa === 'smaa' || aa === 'msaa' || aa === 'fxaa' || aa === 'taa';
  const enabledFx = settings.enabled ?? false;
  if (!enabledFx && !wantAa) return null;

  const effects: React.ReactElement[] = [];

  if (enabledFx && postFxNeedsHdrComposer(settings)) {
    effects.push(<ToneMapping key="tm" mode={getToneMappingMode(settings.toneMapping)} />);
  }

  if (enabledFx && settings.dof?.enabled) {
    effects.push(
      <DepthOfField
        key="dof"
        focusDistance={settings.dof.focusDistance}
        focalLength={settings.dof.focalLength}
        bokehScale={settings.dof.bokehScale}
      />,
    );
  }
  if (settings.bloom?.enabled) {
    effects.push(
      <Bloom
        key="bloom"
        intensity={settings.bloom.intensity}
        luminanceThreshold={settings.bloom.threshold}
        luminanceSmoothing={settings.bloom.radius}
      />,
    );
  }
  if (enabledFx && settings.vignette?.enabled) {
    effects.push(
      <Vignette
        key="vig"
        offset={settings.vignette.offset}
        darkness={settings.vignette.darkness}
        blendFunction={BlendFunction.NORMAL}
      />,
    );
  }
  if (enabledFx && settings.chromaticAberration?.enabled) {
    effects.push(
      <ChromaticAberration
        key="ca"
        offset={new THREE.Vector2(settings.chromaticAberration.offset, settings.chromaticAberration.offset)}
        blendFunction={BlendFunction.NORMAL}
      />,
    );
  }
  if (enabledFx && settings.ssao?.enabled) {
    const s = settings.ssao;
    effects.push(
      <N8AO
        ref={n8aoRef}
        key={`n8ao-${s.radius}-${s.intensity}-${s.samples ?? 16}-${s.denoiseSamples ?? 8}-${s.denoiseRadius ?? 12}-${s.distanceFalloff ?? 1}`}
        aoRadius={s.radius}
        intensity={s.intensity}
        aoSamples={s.samples ?? 16}
        denoiseSamples={s.denoiseSamples ?? 8}
        denoiseRadius={s.denoiseRadius ?? 12}
        distanceFalloff={s.distanceFalloff ?? 1}
        screenSpaceRadius={s.screenSpaceRadius ?? false}
        depthAwareUpsampling
      />,
    );
  }
  if (enabledFx && settings.sharpenBlur?.enabled) {
    effects.push(
      <SharpenBlurPass
        key="sharpenBlur"
        sharpenAmount={Math.min(settings.sharpenBlur.sharpenAmount, 1.0)}
        sharpenRadius={settings.sharpenBlur.sharpenRadius}
        blurRadius={settings.sharpenBlur.blurRadius}
      />,
    );
  }
  if (aa === 'fxaa') {
    effects.push(<FXAA key="fxaa" />);
  }
  if (aa === 'smaa') {
    effects.push(<TunableSmaa key="smaa" preset={settings.smaaPreset ?? 'ultra'} />);
  }

  // Progressive-SSAA accumulator — after MSAA+SMAA so it averages the fully resolved
  // frame. Pass-through until the camera is idle and jitter runs.
  // For `taa` mode this same pass IS the anti-aliasing.
  if (taaResolve && taaRef) {
    effects.push(<EditorTemporalResolvePass key="taa" stateRef={taaRef} />);
  }

  const multisampling = viewerResolveComposerMultisampling(aa, settings, gl);
  // MSAA-only still needs the composer (RenderPass → multisampled RT) even with no effect children.
  if (effects.length === 0 && multisampling === 0) return null;

  // HalfFloat + MSAA fails allocation on many GPUs; byte buffers keep MSAA alive.
  // Keep HalfFloat when not multisampling so bloom/HDR tone-map still have headroom.
  const frameBufferType = multisampling > 0 ? THREE.UnsignedByteType : THREE.HalfFloatType;

  return (
    <EffectComposer
      key={`pp-${aa ?? 'none'}-${settings.smaaPreset ?? 'ultra'}-${multisampling}`}
      multisampling={multisampling}
      depthBuffer
      frameBufferType={frameBufferType}
    >
      {effects}
    </EffectComposer>
  );
}

// ---------------------------------------------------------------------------
// Transmission quality – see transmission-blur-patch.ts
// ---------------------------------------------------------------------------
function TransmissionQuality({ scale = 2 }: { scale?: number }) {
  const { gl, size } = useThree();
  useEffect(() => {
    const dpr = gl.getPixelRatio?.() || 1;
    const safe = clampTransmissionResolutionScale(
      scale,
      gl,
      size.width * dpr,
      size.height * dpr,
    );
    (gl as any).transmissionResolutionScale = safe;
    return () => { (gl as any).transmissionResolutionScale = 1; };
  }, [gl, scale, size.width, size.height]);
  return null;
}

// ---------------------------------------------------------------------------
// Stronger geometric specular anti-aliasing (variance-based)
// ---------------------------------------------------------------------------
(() => {
  const chunk = THREE.ShaderChunk as Record<string, string>;
  const key = 'lights_physical_fragment';
  if (!chunk[key]) return;
  const weak = 'vec3 dxy = max( abs( dFdx( nonPerturbedNormal ) ), abs( dFdy( nonPerturbedNormal ) ) );\nfloat geometryRoughness = max( max( dxy.x, dxy.y ), dxy.z );';
  // Milder than the old *28 / 0.4 cap — that washed anisotropy into round blobs on
  // high-curvature Array Radial ribs while the smooth material-shelf sphere still looked brushed.
  const strong = [
    'vec3 dNdx = dFdx( nonPerturbedNormal );',
    'vec3 dNdy = dFdy( nonPerturbedNormal );',
    'float geometryRoughness = min( ( dot( dNdx, dNdx ) + dot( dNdy, dNdy ) ) * 4.0, 0.12 );',
  ].join('\n');
  if (chunk[key].includes(weak)) {
    chunk[key] = chunk[key].replace(weak, strong);
  }
})();

function ExposureController({
  exposure,
  toneMapping,
  applyRendererToneMap,
}: {
  exposure: number;
  toneMapping?: string;
  applyRendererToneMap?: boolean;
}) {
  const { gl, invalidate } = useThree();
  useEffect(() => {
    gl.toneMappingExposure = exposure;
    if (applyRendererToneMap) {
      gl.toneMapping = getThreeToneMapping(toneMapping);
    }
    invalidate();
  }, [gl, exposure, toneMapping, applyRendererToneMap, invalidate]);
  return null;
}

/**
 * Clears / fills the GL buffer. Solid colors must live *inside* the scene
 * (not as a CSS layer behind a transparent canvas) — otherwise MSAA / composer
 * resolve against clear-black alpha and fringe over the CSS bg, which reads as
 * an unsharp-mask / sharpening halo on every silhouette.
 */
function SceneBgController({
  transparent,
  solidColor,
}: {
  transparent: boolean;
  /** When set (and not transparent), used as opaque scene.background + clear color. */
  solidColor?: string;
}) {
  const { gl, scene } = useThree();

  useEffect(() => {
    if (transparent) {
      scene.background = null;
      gl.setClearColor(0x000000, 0);
      return;
    }
    if (solidColor) {
      const c = new THREE.Color(solidColor);
      scene.background = c;
      gl.setClearColor(c, 1);
      return;
    }
    // Environment / image modes own scene.background themselves.
    gl.setClearColor(0x000000, 1);
  }, [transparent, solidColor, gl, scene]);

  return null;
}

/** Bake a CSS-like gradient into scene.background so AA samples the real colors. */
function SceneGradientBackground({
  gradient,
}: {
  gradient: { type: string; color1: string; color2: string; angle: number };
}) {
  const { scene, size } = useThree();
  const texRef = useRef<THREE.CanvasTexture | null>(null);

  useEffect(() => {
    if (size.width < 1 || size.height < 1) return;
    const c = document.createElement('canvas');
    const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
    c.width = Math.min(Math.max(1, Math.round(size.width * dpr)), 2048);
    c.height = Math.min(Math.max(1, Math.round(size.height * dpr)), 2048);
    const ctx = c.getContext('2d')!;
    if (gradient.type === 'radial') {
      const g = ctx.createRadialGradient(
        c.width / 2, c.height / 2, 0,
        c.width / 2, c.height / 2, Math.max(c.width, c.height) / 2,
      );
      g.addColorStop(0, gradient.color1);
      g.addColorStop(1, gradient.color2);
      ctx.fillStyle = g;
    } else {
      const rad = ((gradient.angle ?? 180) * Math.PI) / 180;
      const x0 = c.width / 2 - Math.cos(rad) * c.width;
      const y0 = c.height / 2 - Math.sin(rad) * c.height;
      const x1 = c.width / 2 + Math.cos(rad) * c.width;
      const y1 = c.height / 2 + Math.sin(rad) * c.height;
      const g = ctx.createLinearGradient(x0, y0, x1, y1);
      g.addColorStop(0, gradient.color1);
      g.addColorStop(1, gradient.color2);
      ctx.fillStyle = g;
    }
    ctx.fillRect(0, 0, c.width, c.height);
    const tex = new THREE.CanvasTexture(c);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.needsUpdate = true;
    const prev = texRef.current;
    texRef.current = tex;
    scene.background = tex;
    prev?.dispose();
    return () => {
      if (scene.background === tex) scene.background = null;
      tex.dispose();
      if (texRef.current === tex) texRef.current = null;
    };
  }, [scene, size.width, size.height, gradient.type, gradient.color1, gradient.color2, gradient.angle]);

  return null;
}

function SceneBackgroundImage({
  url,
  fit = 'cover',
  brightness = 1,
  contrast = 1,
  saturation = 1,
  temperature = 0,
}: {
  url: string;
  fit?: 'cover' | 'contain' | 'fill';
  brightness?: number;
  contrast?: number;
  saturation?: number;
  temperature?: number;
}) {
  const { scene, size } = useThree();
  const texRef = useRef<THREE.CanvasTexture | null>(null);
  const [img, setImg] = useState<HTMLImageElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    const el = new Image();
    el.crossOrigin = 'anonymous';
    el.onload = () => { if (!cancelled) setImg(el); };
    el.src = url;
    return () => { cancelled = true; setImg(null); };
  }, [url]);

  useEffect(() => {
    if (!img || size.width === 0 || size.height === 0) return;

    const c = document.createElement('canvas');
    c.width = Math.min(size.width * (typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1), 2048);
    c.height = Math.min(size.height * (typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1), 2048);
    const ctx = c.getContext('2d')!;
    ctx.fillStyle = '#1a1a1e';
    ctx.fillRect(0, 0, c.width, c.height);

    const fp: string[] = [];
    if (brightness !== 1) fp.push(`brightness(${brightness})`);
    if (contrast !== 1) fp.push(`contrast(${contrast})`);
    if (saturation !== 1) fp.push(`saturate(${saturation})`);
    if (temperature !== 0) {
      fp.push(`sepia(${Math.min(Math.abs(temperature), 100)}%)`);
      if (temperature < 0) fp.push(`hue-rotate(180deg)`);
    }
    ctx.filter = fp.length > 0 ? fp.join(' ') : 'none';

    const iA = img.naturalWidth / img.naturalHeight;
    const vA = c.width / c.height;
    let dx = 0, dy = 0, dw = c.width, dh = c.height;
    if (fit === 'cover') {
      if (iA > vA) { dw = c.height * iA; dx = (c.width - dw) / 2; }
      else { dh = c.width / iA; dy = (c.height - dh) / 2; }
    } else if (fit === 'contain') {
      if (iA > vA) { dh = c.width / iA; dy = (c.height - dh) / 2; }
      else { dw = c.height * iA; dx = (c.width - dw) / 2; }
    }
    ctx.drawImage(img, dx, dy, dw, dh);

    texRef.current?.dispose();
    const tex = new THREE.CanvasTexture(c);
    tex.colorSpace = THREE.SRGBColorSpace;
    texRef.current = tex;
    scene.background = tex;
  }, [img, fit, size.width, size.height, scene, brightness, contrast, saturation, temperature]);

  useEffect(() => {
    return () => {
      if (scene.background === texRef.current) scene.background = null;
      texRef.current?.dispose();
    };
  }, [scene]);

  return null;
}

// ---------------------------------------------------------------------------
// Snapshots at an explicit output size
// ---------------------------------------------------------------------------

type SnapshotCaptureFn = (options?: SnapshotCaptureOptions) => Promise<SnapshotCaptureResult | null>;

/** Runtime handles the capture needs from inside the Canvas tree. */
interface SnapshotRuntime {
  gl: THREE.WebGLRenderer;
  invalidate: () => void;
  /** Canvas size in CSS pixels; the drawing buffer is this times the pixel ratio. */
  cssWidth: number;
  cssHeight: number;
  dpr: number;
}

/**
 * MSAA output buffers plus transmission render targets already dominate the GPU
 * budget; past this the allocation fails outright instead of degrading, so a
 * request is clamped rather than attempted.
 */
const SNAPSHOT_MAX_EDGE = 4096;
const SNAPSHOT_MAX_PIXELS = 16_000_000;
const SNAPSHOT_MIN_EDGE = 16;
/**
 * Ceiling for the temporarily inflated drawing buffer that feeds a snapshot.
 * MSAA multiplies this several times over in GPU memory, so past roughly this
 * point the allocation is the thing that fails, not the frame rate.
 */
const SNAPSHOT_MAX_SOURCE_PIXELS = 12_000_000;
const SNAPSHOT_MAX_DPR = 8;

function snapshotNextFrame(): Promise<void> {
  return new Promise((resolve) => requestAnimationFrame(() => resolve()));
}

function clampSnapshotSize(width: number, height: number, gl: THREE.WebGLRenderer) {
  const maxTexture = gl.capabilities?.maxTextureSize ?? SNAPSHOT_MAX_EDGE;
  const maxEdge = Math.max(SNAPSHOT_MIN_EDGE, Math.min(SNAPSHOT_MAX_EDGE, maxTexture));
  let w = Math.min(Math.max(Math.round(width) || 0, SNAPSHOT_MIN_EDGE), maxEdge);
  let h = Math.min(Math.max(Math.round(height) || 0, SNAPSHOT_MIN_EDGE), maxEdge);
  const px = w * h;
  if (px > SNAPSHOT_MAX_PIXELS) {
    const k = Math.sqrt(SNAPSHOT_MAX_PIXELS / px);
    w = Math.max(SNAPSHOT_MIN_EDGE, Math.floor(w * k));
    h = Math.max(SNAPSHOT_MIN_EDGE, Math.floor(h * k));
  }
  return { width: w, height: h };
}

/**
 * Pixel ratio needed for the live viewport to supply at least the requested
 * pixels, so the snapshot is a downsample rather than an upscale. The limiting
 * axis depends on the fit: `contain` is bounded by the tighter ratio, `cover` by
 * the looser one, and asking for more than that buys nothing but a slower frame
 * and a much larger MSAA buffer. Capped by total buffer pixels, since the
 * composer output and transmission targets scale with it too.
 */
function snapshotSourceDpr(
  cssWidth: number,
  cssHeight: number,
  target: { width: number; height: number },
  currentDpr: number,
  fit: 'contain' | 'cover',
): number {
  const w = Math.max(cssWidth, 1);
  const h = Math.max(cssHeight, 1);
  const needed = fit === 'cover'
    ? Math.max(target.width / w, target.height / h)
    : Math.min(target.width / w, target.height / h);
  const byPixels = Math.sqrt(SNAPSHOT_MAX_SOURCE_PIXELS / (w * h));
  const cap = Math.min(SNAPSHOT_MAX_DPR, byPixels);
  // Never drop below what is already on screen, even if the cap is lower.
  return Math.max(currentDpr, Math.min(needed, Math.max(cap, currentDpr)));
}

/**
 * Backdrop colour as actually rendered, read from the corner of the live frame.
 * The authored scene colour cannot be used directly for letterbox bars: tone
 * mapping means `#ece0d4` leaves the composer as a visibly different pixel, which
 * would show as a seam between the bars and the render. Returns null when the
 * corner is transparent, so alpha backgrounds stay transparent.
 */
function sampleRenderedBackdrop(source: HTMLCanvasElement): string | null {
  try {
    const probe = document.createElement('canvas');
    probe.width = 1;
    probe.height = 1;
    const ctx = probe.getContext('2d', { willReadFrequently: true });
    if (!ctx) return null;
    ctx.drawImage(source, 0, 0, 1, 1, 0, 0, 1, 1);
    const [r, g, b, a] = ctx.getImageData(0, 0, 1, 1).data;
    if (a < 8) return null;
    return `rgb(${r}, ${g}, ${b})`;
  } catch {
    return null;
  }
}

/**
 * Scales the live frame into an exactly sized output frame. `contain` letterboxes
 * with the background colour so a different output aspect never crops the
 * product; `cover` fills and crops instead.
 */
function drawSnapshot(
  source: HTMLCanvasElement,
  target: { width: number; height: number },
  fit: 'contain' | 'cover',
  background: string | undefined,
  mimeType: string,
  quality?: number,
): { dataUrl: string; scale: number } | null {
  const out = document.createElement('canvas');
  out.width = target.width;
  out.height = target.height;
  const ctx = out.getContext('2d');
  if (!ctx) return null;

  // JPEG has no alpha, so transparent pixels would otherwise encode as black.
  const backdrop = background ? sampleRenderedBackdrop(source) ?? background : undefined;
  const fill = backdrop ?? (mimeType === 'image/jpeg' ? '#ffffff' : undefined);
  if (fill) {
    ctx.fillStyle = fill;
    ctx.fillRect(0, 0, out.width, out.height);
  }

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  const sw = Math.max(source.width, 1);
  const sh = Math.max(source.height, 1);
  const scale = fit === 'cover'
    ? Math.max(target.width / sw, target.height / sh)
    : Math.min(target.width / sw, target.height / sh);
  const dw = sw * scale;
  const dh = sh * scale;
  ctx.drawImage(source, (target.width - dw) / 2, (target.height - dh) / 2, dw, dh);

  const dataUrl = mimeType === 'image/jpeg'
    ? out.toDataURL('image/jpeg', quality ?? 0.92)
    : out.toDataURL(mimeType);
  return { dataUrl, scale };
}

/** Publishes the renderer handles a snapshot needs out to the ViewerCanvas shell. */
function SnapshotRuntimeBridge({ onRuntime }: { onRuntime: (runtime: SnapshotRuntime | null) => void }) {
  const gl = useThree((s) => s.gl);
  const invalidate = useThree((s) => s.invalidate);
  const size = useThree((s) => s.size);
  const dpr = useThree((s) => s.viewport.dpr);

  useEffect(() => {
    onRuntime({ gl, invalidate, cssWidth: size.width, cssHeight: size.height, dpr });
    return () => onRuntime(null);
  }, [gl, invalidate, size.width, size.height, dpr, onRuntime]);

  return null;
}

export function ViewerCanvas({
  children,
  environmentPreset = 'studio',
  hdrUrl,
  backgroundColor = '#f5f5f5',
  shadows = true,
  className,
  autoRotate = false,
  autoRotateMode,
  autoRotateSpeed = 1,
  onCreated,
  cameraPosition,
  cameraTarget,
  cameraFov,
  cameraNear,
  cameraFar,
  cameraLensShiftX,
  cameraLensShiftY,
  orbitTarget,
  cameraMinDistance,
  cameraMaxDistance,
  renderAspect,
  envIntensity,
  envBackgroundBlur,
  envShowBackground,
  envLinkToCamera = false,
  envRotationY = 0,
  envFlipH = false,
  envFlipV = false,
  bgMode = 'environment',
  bgSolidColor,
  bgGradient,
  bgImageUrl,
  bgImageFit = 'cover',
  bgImageAdjust,
  postfx,
  postfxTaaResetKey,
  postfxTaaSoftResetKey,
  onSceneStats,
  onCameraChange,
  dpr,
  transmissionResolutionScale = 2,
  glOverrides,
  debugCheckShaderErrors,
  introSpinEnabled = true,
  introSpin = false,
  introSpinKey,
  introSpinAngle = 0.19,
  introSpinDuration = 1.05,
  ref,
}: ViewerCanvasProps & { ref?: React.Ref<ViewerCanvasHandle> }) {
  const glRef = useRef<THREE.WebGLRenderer | null>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const cameraRef = useRef<THREE.Camera | null>(null);
  const [webglLost, setWebglLost] = useState(false);

  const snapshotRuntimeRef = useRef<SnapshotRuntime | null>(null);
  const handleSnapshotRuntime = useCallback((runtime: SnapshotRuntime | null) => {
    snapshotRuntimeRef.current = runtime;
  }, []);
  /** Non-null only while a snapshot renders; overrides the viewer pixel ratio. */
  const [snapshotDpr, setSnapshotDpr] = useState<number | null>(null);
  const snapshotBusyRef = useRef(false);

  const initialPosition: [number, number, number] = cameraPosition || [2, 1.5, 2];
  const initialAspect = renderAspect && renderAspect > 0 ? renderAspect : 16 / 9;
  const initialFov = cameraFov ? hfovToVfov(cameraFov, initialAspect) : 50;
  const initialNear = Math.max(cameraNear || 0.1, 0.01);
  const initialFar = Math.min(cameraFar || 1000, 500);
  const effectiveMode = autoRotateMode || (autoRotate ? 'rotate' : 'off');
  const isAutoRotating = effectiveMode !== 'off';

  const orbitTargetVec = useMemo(() => {
    const arr = orbitTarget || cameraTarget;
    if (!arr) return undefined;
    return new THREE.Vector3(arr[0], arr[1], arr[2]);
  }, [orbitTarget?.[0], orbitTarget?.[1], orbitTarget?.[2], cameraTarget?.[0], cameraTarget?.[1], cameraTarget?.[2]]); // eslint-disable-line react-hooks/exhaustive-deps
  const showHdriBg = bgMode === 'environment' && (envShowBackground ?? false);
  const useSceneBgImage = bgMode === 'image' && !!bgImageUrl;
  const useSceneSolidBg = bgMode === 'solid';
  const useSceneGradientBg = bgMode === 'gradient' && !!bgGradient;
  // Only the checkerboard "transparent" mode needs CSS-behind-canvas compositing.
  // Solid/gradient used to do that too — MSAA then fringed against clear alpha and
  // looked like a permanent sharpening filter on every silhouette.
  const needTransparentGL = bgMode === 'transparent';
  const solidBgColor = useSceneSolidBg ? (bgSolidColor || backgroundColor) : undefined;

  /** Letterbox fill for snapshots. Left undefined in transparent mode so PNGs keep alpha. */
  const snapshotBackground = needTransparentGL ? undefined : (solidBgColor ?? backgroundColor);

  /**
   * Captures at an exact output size by briefly rendering the live view at a
   * higher pixel ratio and scaling that frame down, so the image comes from the
   * real pipeline (ACES tone mapping, MSAA, SMAA) instead of a second renderer.
   * Only the pixel ratio changes, so page layout and canvas CSS size are untouched.
   */
  const captureSnapshot = useCallback<SnapshotCaptureFn>(
    async (options = {}) => {
      const runtime = snapshotRuntimeRef.current;
      if (!runtime || snapshotBusyRef.current) return null;
      const canvas = runtime.gl.domElement;
      if (!canvas) return null;

      snapshotBusyRef.current = true;
      try {
        const mimeType = options.mimeType === 'image/jpeg' ? 'image/jpeg' : 'image/png';
        const fit = options.fit === 'cover' ? 'cover' : 'contain';
        const target = clampSnapshotSize(
          options.width ?? canvas.width,
          options.height ?? canvas.height,
          runtime.gl,
        );
        const wanted = snapshotSourceDpr(runtime.cssWidth, runtime.cssHeight, target, runtime.dpr, fit);

        if (wanted > runtime.dpr + 0.01) {
          setSnapshotDpr(wanted);
          // R3F applies the ratio and the composer resizes its buffers in effects,
          // so wait for the drawing buffer to actually grow before reading it.
          const expected = Math.floor(Math.max(runtime.cssWidth, 1) * wanted) - 2;
          // Bounded by wall clock as well as frames: on a slow renderer 90 frames
          // is many seconds, and the capture still succeeds at the live ratio.
          const deadline = Date.now() + 8000;
          for (let i = 0; i < 90 && Date.now() < deadline; i++) {
            await snapshotNextFrame();
            if (canvas.width >= expected) break;
          }
        }

        // The frameloop is demand-driven: force a frame at the capture ratio.
        runtime.invalidate();
        await snapshotNextFrame();
        await snapshotNextFrame();

        const drawn = drawSnapshot(canvas, target, fit, snapshotBackground, mimeType, options.quality);
        if (!drawn) return null;
        return {
          dataUrl: drawn.dataUrl,
          width: target.width,
          height: target.height,
          pixelRatio: canvas.width / Math.max(runtime.cssWidth, 1),
          upscaled: drawn.scale > 1.001,
        };
      } catch {
        return null;
      } finally {
        setSnapshotDpr(null);
        snapshotRuntimeRef.current?.invalidate();
        await snapshotNextFrame();
        snapshotBusyRef.current = false;
      }
    },
    [snapshotBackground],
  );

  useImperativeHandle(
    ref,
    () => ({
      screenshot: () => glRef.current?.domElement.toDataURL('image/png') ?? null,
      captureSnapshot,
    }),
    [captureSnapshot],
  );

  const bgFilter = (() => {
    if (bgMode !== 'image' || useSceneBgImage || !bgImageAdjust) return undefined;
    const { brightness = 1, contrast = 1, saturation = 1, temperature = 0 } = bgImageAdjust;
    const parts: string[] = [];
    if (brightness !== 1) parts.push(`brightness(${brightness})`);
    if (contrast !== 1) parts.push(`contrast(${contrast})`);
    if (saturation !== 1) parts.push(`saturate(${saturation})`);
    if (temperature !== 0) {
      const t = Math.abs(temperature);
      parts.push(`sepia(${Math.min(t, 100)}%)`);
      if (temperature < 0) parts.push(`hue-rotate(180deg)`);
    }
    return parts.length > 0 ? parts.join(' ') : undefined;
  })();

  // Idle SSAA (below) drives the pixel ratio at runtime: modest while orbiting,
  // higher once still. Give the Canvas a single stable "interact" DPR so R3F's
  // tuple-based adaptive-perf scaling doesn't fight our manual setDpr.
  const idleSsaaEnabled = dpr == null && postfx?.antialiasing !== 'taa';

  const devicePixelRatioSafe = useMemo(() => {
    if (typeof window === 'undefined') return 1;
    return window.devicePixelRatio || 1;
  }, []);

  const interactDpr = useMemo(() => {
    const lo = Math.max(2, Math.min(postfx?.dprMin ?? 2, postfx?.dprMax ?? 2.5));
    const hi = Math.max(lo, postfx?.dprMax ?? 2.5);
    const spatialCeil = Math.min(Math.max(hi, 2), 2 * devicePixelRatioSafe, 2.5);
    return Math.max(2, lo, spatialCeil);
  }, [devicePixelRatioSafe, postfx?.dprMin, postfx?.dprMax]);

  // No idle DPR bounce — temporal history AA ghosts glass; spatial dip looked worse.
  const desiredIdleDpr = interactDpr;

  // Idle SSAA drives this at runtime (interact → idle). Single source of truth so
  // R3F's per-commit re-application of the `dpr` prop doesn't fight our updates.
  const [renderDpr, setRenderDpr] = useState(interactDpr);
  useEffect(() => {
    if (idleSsaaEnabled) setRenderDpr(interactDpr);
  }, [idleSsaaEnabled, interactDpr]);

  const [graphTexBusy, setGraphTexBusy] = useState(true);
  const handleGraphTexBusy = useCallback((busy: boolean) => setGraphTexBusy(busy), []);
  const [dprResetEpoch, setDprResetEpoch] = useState(0);
  const prevRenderDprRef = useRef(renderDpr);
  useEffect(() => {
    if (Math.abs(prevRenderDprRef.current - renderDpr) > 0.02) {
      prevRenderDprRef.current = renderDpr;
      setDprResetEpoch((n) => n + 1);
    }
  }, [renderDpr]);
  const taaHardResetKey = useMemo(
    () => `${postfxTaaResetKey ?? ''}|dpr:${dprResetEpoch}`,
    [postfxTaaResetKey, dprResetEpoch],
  );

  // Progressive-refine-when-still: once idle, jitter sub-pixel and average samples (SSAA).
  const [isIdle, setIsIdle] = useState(false);
  /** Intro park + spin — keep idle SSAA off so history never smears across the orbit. */
  const [introOrbitBusy, setIntroOrbitBusy] = useState(false);
  const idleRearmTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const introOrbitBusyRef = useRef(false);
  introOrbitBusyRef.current = introOrbitBusy;
  const handleIdleFromSampler = useCallback((idle: boolean) => {
    if (idleRearmTimerRef.current) {
      clearTimeout(idleRearmTimerRef.current);
      idleRearmTimerRef.current = null;
    }
    // Never mark idle while the intro is parked/spinning.
    setIsIdle(idle && !introOrbitBusyRef.current);
  }, []);
  /** Motion without OrbitControls `end` (damping / intro) must re-arm idle AA. */
  const handleIdleInterrupted = useCallback(() => {
    setIsIdle(false);
    if (idleRearmTimerRef.current) clearTimeout(idleRearmTimerRef.current);
    idleRearmTimerRef.current = setTimeout(() => {
      idleRearmTimerRef.current = null;
      if (!introOrbitBusyRef.current) setIsIdle(true);
    }, 320);
  }, []);
  const handleIntroOrbitBusy = useCallback((busy: boolean) => {
    setIntroOrbitBusy(busy);
    if (busy) {
      setIsIdle(false);
      if (idleRearmTimerRef.current) {
        clearTimeout(idleRearmTimerRef.current);
        idleRearmTimerRef.current = null;
      }
    } else {
      // Re-arm idle AA shortly after the intro lands on the authored pose.
      if (idleRearmTimerRef.current) clearTimeout(idleRearmTimerRef.current);
      idleRearmTimerRef.current = setTimeout(() => {
        idleRearmTimerRef.current = null;
        if (!introOrbitBusyRef.current) setIsIdle(true);
      }, 320);
    }
  }, []);
  const handleViewportResize = useCallback(() => {
    // A snapshot inflates the pixel ratio on purpose; treating that as a viewport
    // change would reset the pixel ratio mid-capture and fight the capture.
    if (snapshotBusyRef.current) return;
    setIsIdle(false);
    setRenderDpr(interactDpr);
    setDprResetEpoch((n) => n + 1);
    if (idleRearmTimerRef.current) {
      clearTimeout(idleRearmTimerRef.current);
      idleRearmTimerRef.current = null;
    }
    // Wide layouts ghost more if accumulate re-arms during the layout settle —
    // wait a bit longer after DevTools / panel chrome changes.
    idleRearmTimerRef.current = setTimeout(() => {
      idleRearmTimerRef.current = null;
      if (!introOrbitBusyRef.current) setIsIdle(true);
    }, 480);
  }, [interactDpr]);
  useEffect(() => () => {
    if (idleRearmTimerRef.current) clearTimeout(idleRearmTimerRef.current);
  }, []);
  // Temporal history AA ghosts glass on this product. Idle DPR bouncing also made
  // edges softer than the stable pre–temp-AA path. Stay on constant high DPR + MSAA/SMAA.
  const idleAccumFeature = false;
  const accumulate = false;
  const idleSpatialFeature = false;

  const canvasDpr = useMemo(() => {
    // Re-applied on every Canvas render, which is what makes the snapshot ratio
    // hold instead of being reset by an unrelated re-render mid-capture.
    if (snapshotDpr != null) return snapshotDpr;
    if (dpr != null) return dpr;
    let lo = Math.min(postfx?.dprMin ?? 1.5, postfx?.dprMax ?? 2.5);
    let hi = Math.max(postfx?.dprMin ?? 1.5, postfx?.dprMax ?? 2.5);
    // TAA keeps a full-res history RT + continuous frames — keep DPR modest to avoid
    // WebGL "allocation too large" / incomplete framebuffer cascades on big viewports.
    if (postfx?.antialiasing === 'taa') {
      lo = Math.min(lo, 2);
      hi = Math.min(hi, 2);
    }
    if (idleSsaaEnabled) return renderDpr;
    return [lo, hi] as [number, number];
  }, [snapshotDpr, dpr, postfx?.dprMin, postfx?.dprMax, postfx?.antialiasing, idleSsaaEnabled, renderDpr]);

  const canvasGl = useMemo(
    () => ({
      alpha: true,
      preserveDrawingBuffer: true,
      ...glOverrides,
      antialias:
        glOverrides?.antialias !== undefined
          ? glOverrides.antialias
          : nativeCanvasMsaa(postfx),
      powerPreference: glOverrides?.powerPreference ?? 'high-performance',
    }),
    [glOverrides, postfx],
  );

  const canvasBg = (() => {
    switch (bgMode) {
      case 'solid': return bgSolidColor || backgroundColor;
      case 'gradient': {
        if (!bgGradient) return backgroundColor;
        if (bgGradient.type === 'radial') return `radial-gradient(circle, ${bgGradient.color1}, ${bgGradient.color2})`;
        return `linear-gradient(${bgGradient.angle}deg, ${bgGradient.color1}, ${bgGradient.color2})`;
      }
      case 'image': return backgroundColor;
      case 'transparent': {
        const sz = 10;
        return `repeating-conic-gradient(#808080 0% 25%, #c0c0c0 0% 50%) 0 0 / ${sz}px ${sz}px`;
      }
      default: return backgroundColor;
    }
  })();

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }} className={className}>
      {needTransparentGL && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: canvasBg,
            filter: bgFilter,
            zIndex: 0,
          }}
        />
      )}
    <Canvas
      key={
        postFxNeedsComposer(postfx) || postfx?.antialiasing === 'taa'
          ? 'gl-composer'
          : nativeCanvasMsaa(postfx)
            ? 'gl-native-msaa'
            : 'gl-no-aa'
      }
      frameloop={postfx?.antialiasing === 'taa' ? 'always' : 'demand'}
      shadows={shadows ? ('percentage' as const) : false}
      camera={{ position: initialPosition, fov: initialFov, near: initialNear, far: initialFar }}
      dpr={canvasDpr}
      gl={canvasGl}
      style={{
        position: needTransparentGL ? 'absolute' : undefined,
        inset: needTransparentGL ? 0 : undefined,
        zIndex: needTransparentGL ? 1 : undefined,
        background: needTransparentGL ? 'transparent' : canvasBg,
      }}
      onCreated={(state) => {
        glRef.current = state.gl;
        sceneRef.current = state.scene;
        cameraRef.current = state.camera;
        patchCompileAsyncOnRenderer(state.gl);
        // Always off in the viewer: checkShaderErrors calls getProgramParameter/InfoLog
        // after every link and races material swaps → GL_INVALID_VALUE spam in the console.
        // Opt in only via explicit debugCheckShaderErrors={true}.
        state.gl.debug.checkShaderErrors = debugCheckShaderErrors === true;
        const canvas = state.gl.domElement;
        const onLost = (e: Event) => {
          e.preventDefault();
          setWebglLost(true);
        };
        const onRestored = () => {
          setWebglLost(false);
          state.invalidate();
        };
        canvas.addEventListener('webglcontextlost', onLost, false);
        canvas.addEventListener('webglcontextrestored', onRestored, false);
        onCreated?.();
      }}
      data-screenshot-canvas
    >
      <ViewerGlProgramGuard />
      <TextureReadyListener />
      <SnapshotRuntimeBridge onRuntime={handleSnapshotRuntime} />
      <CanvasViewportHistoryReset onViewportResize={handleViewportResize} />
      {idleSpatialFeature && <GraphTextureBusyTracker onBusyChange={handleGraphTexBusy} />}
      <ShaderPrecompiler />
      {idleSpatialFeature && (
        <IdleSupersampler
          interactDpr={interactDpr}
          desiredIdleDpr={desiredIdleDpr}
          onDpr={setRenderDpr}
          onIdle={handleIdleFromSampler}
        />
      )}
      {idleAccumFeature && <ViewerIdleMotionGuard onMotion={handleIdleInterrupted} />}
      <ViewerTextureAnisotropyCap
        maxAnisotropy={postfx?.textureMaxAnisotropy ?? 16}
        lodBias={typeof postfx?.textureLodBias === 'number' ? postfx.textureLodBias : 0.9}
      />
      <TransmissionQuality scale={transmissionResolutionScale} />
      {postfx?.exposure != null && (
        <ExposureController
          exposure={postfx.exposure}
          toneMapping={postfx.toneMapping}
          applyRendererToneMap={postFxUsesRendererToneMap(postfx)}
        />
      )}
      <SceneBgController
        transparent={needTransparentGL}
        solidColor={useSceneGradientBg || useSceneBgImage ? undefined : solidBgColor}
      />
      {useSceneGradientBg && bgGradient && (
        <SceneGradientBackground gradient={bgGradient} />
      )}
      {useSceneBgImage && (
        <SceneBackgroundImage
          url={bgImageUrl!}
          fit={bgImageFit}
          brightness={bgImageAdjust?.brightness}
          contrast={bgImageAdjust?.contrast}
          saturation={bgImageAdjust?.saturation}
          temperature={bgImageAdjust?.temperature}
        />
      )}
      <Suspense fallback={null}>
        <ConfigureGltfLoaders />
        {hdrUrl ? (
          <ResolvedSceneEnvironment
            hdrUrl={hdrUrl}
            background={showHdriBg}
            blur={envBackgroundBlur ?? 0}
            environmentIntensity={envIntensity ?? 1}
          />
        ) : (
          <Environment
            preset={environmentPreset as any}
            background={showHdriBg}
            blur={envBackgroundBlur ?? 0}
            environmentIntensity={envIntensity ?? 1}
          />
        )}
        <EnvironmentRotationSync enabled={envLinkToCamera} rotationY={envRotationY} flipH={envFlipH} flipV={envFlipV} />

        <NestedTransmissionApplier />
        {children}
      </Suspense>
      <OrbitControls
        makeDefault
        enablePan
        enableZoom
        enableRotate
        enableDamping
        dampingFactor={0.08}
        autoRotate={isAutoRotating}
        autoRotateSpeed={autoRotateSpeed}
        minPolarAngle={0.00873}
        maxPolarAngle={Math.PI - 0.00873}
        minDistance={cameraMinDistance}
        maxDistance={cameraMaxDistance}
        target={orbitTargetVec}
      />
      <AutoRotateKick enabled={isAutoRotating} />
      <AutoRotateWallClockCorrection enabled={isAutoRotating} />
      {effectiveMode === 'rotate-reset' && (
        <AutoRotateReset
          cameraPosition={cameraPosition}
          orbitTarget={orbitTarget || cameraTarget}
          speed={autoRotateSpeed}
        />
      )}
      <FovSpeedSync />
      {onSceneStats && <SceneStatsReporter onStats={onSceneStats} postfx={postfx} />}
      {postfx &&
        postFxNeedsComposer(postfx) &&
        (postfx.antialiasing === 'taa' ? (
          <EditorTaaRoot enabled resetKey={taaHardResetKey} softResetKey={postfxTaaSoftResetKey}>
            <ViewerPostProcessing settings={postfx} taaResolve />
          </EditorTaaRoot>
        ) : idleAccumFeature ? (
          // Accumulator is always mounted (resolve pass = pass-through until idle);
          // `enabled` gates the jitter/copy so there's no composer remount per gesture.
          <EditorTaaRoot enabled={accumulate} resetKey={taaHardResetKey} softResetKey={postfxTaaSoftResetKey}>
            <ViewerPostProcessing settings={postfx} taaResolve />
          </EditorTaaRoot>
        ) : (
          <ViewerPostProcessing settings={postfx} />
        ))}
      <CameraPresetListener />
      <CameraTransitionListener />
      <IntroOrbitSpin
        enabled={introSpinEnabled}
        active={introSpin}
        spinKey={introSpinKey}
        cameraPosition={cameraPosition}
        orbitTarget={orbitTarget || cameraTarget}
        angle={introSpinAngle}
        duration={introSpinDuration}
        onBusyChange={handleIntroOrbitBusy}
      />
      {/* Always mount — conditional mount when scene loads caused hook-order issues with R3F + React 19 in production */}
      <CameraSync
        cameraPosition={cameraPosition}
        cameraTarget={cameraTarget}
        cameraFov={cameraFov}
        cameraNear={cameraNear}
        cameraFar={cameraFar}
        cameraLensShiftX={cameraLensShiftX}
        cameraLensShiftY={cameraLensShiftY}
        orbitTarget={orbitTarget}
        renderAspect={renderAspect}
        onCameraChange={onCameraChange}
      />
    </Canvas>
      {webglLost && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 20,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 12,
            background: 'rgba(12,10,8,0.88)',
            color: '#fafafa',
            fontFamily: 'system-ui,sans-serif',
            fontSize: 14,
            textAlign: 'center',
            padding: 24,
          }}
        >
          <div>The 3D view ran out of graphics memory and stopped.</div>
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{
              padding: '8px 16px',
              borderRadius: 6,
              border: 'none',
              background: '#fafafa',
              color: '#18181b',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Reload
          </button>
        </div>
      )}
    </div>
  );
}
